import React, { useState } from 'react';
import {
    Typography,
    Box,
    Link,
    Stack,
    Collapse
} from '@mui/material';
import {
    Warning as WarningIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';
import SourceButton from '../PhoneTab/SourceButton';
import _ from 'lodash';
import { Chip } from '@mui/material';
import { colors } from '../../styles/globals.js';
import { AddEmailButton, RemoveEmailButton } from '../EditMode/CompanyDetailEditControls';

const StatusItem = ({ type, text }) => {
    const isWarning = type === 'warning';
    const isSuccess = type === 'success';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isWarning && (
                <WarningIcon sx={{ fontSize: 16, color: '#EF4444' }} />
            )}
            {isSuccess && (
                <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
            )}
            <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem' }}>
                {text}
            </Typography>
        </Box>
    );
};

const EmailItem = ({ email, isInputData }) => {
    const { value, assessments = [], source = [] } = email;
    const [expanded, setExpanded] = useState(false);

    const handleItemClick = () => {
        setExpanded(!expanded);
    };

    const handleEmailClick = (e) => {
        e.stopPropagation(); // Prevent the item click handler from firing
        // Allow mailto: link to work normally
    };

    return (
        <Box sx={{
            mb: 1,
            border: '1px solid #E2E6F0',
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(242, 243, 251, 0.4)',
            }
        }}>
            {/* Main content area */}
            <Box
                onClick={handleItemClick}
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                {/* Left section with email info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Email address */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '1rem', letterSpacing: '0.3px' }}>
                            <Link
                                href={`mailto:${value}`}
                                underline="hover"
                                onClick={handleEmailClick}
                                sx={{
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                {_.toLower(value)}
                            </Link>
                        </Typography>
                        {isInputData && (
                            <Chip label="Input Data" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontSize: '0.75rem' }} />
                        )}
                    </Box>
                </Box>

                {/* Right section with source */}
                <Box
                    sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SourceButton sources={source} />
                </Box>
            </Box>
            {/* Collapsible Status items - Full width */}
            <Collapse in={expanded} timeout={300}>
                <Box sx={{
                    borderTop: '1px solid #e0e0e0',
                    px: 2,
                    pb: 2,
                    pt: 1
                }}>
                    <Stack spacing={0.5}>
                        {_.uniqBy(assessments, 'ui_string').map((assessment, index) => {
                            if (!assessment.ui_string || assessment.ui_string === "None") return null;

                            const score = parseFloat(assessment.score);
                            const statusType = score > 1.0 ? 'warning' : 'success';

                            return (
                                <StatusItem
                                    key={index}
                                    type={statusType}
                                    text={assessment.ui_string}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    );
};

const EmailTab = ({ emails, onUpdateEmails }) => {

    if (!emails || emails.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: colors.textSecondary }}>
                    No email data found
                </Typography>
                {onUpdateEmails && (
                    <Box sx={{ mt: 2 }}>
                        <AddEmailButton emails={emails || []} onUpdate={onUpdateEmails} />
                    </Box>
                )}
            </Box>
        );
    }

    // Sort emails so input data appears first
    const sortedEmails = _.sortBy(emails, (email) => {
        const isInputData = email.association_type && email.association_type.includes('INPUT_DATA');
        return isInputData ? 0 : 1; // Input data gets 0 (first), others get 1 (second)
    });

    return (
        <Box>
            {onUpdateEmails && (
                <Box sx={{ mb: 2 }}>
                    <AddEmailButton emails={emails} onUpdate={onUpdateEmails} />
                </Box>
            )}
            {_.map(sortedEmails, (email, index) => {
                const isInputData = email.association_type && email.association_type.includes('INPUT_DATA');

                return (
                    <Box key={email.id || index} sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <EmailItem email={email} isInputData={isInputData} />
                        </Box>
                        {onUpdateEmails && (
                            <RemoveEmailButton
                                emailId={email.id}
                                emails={emails}
                                onUpdate={onUpdateEmails}
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default EmailTab;